import dayjs from 'dayjs';

import {
  QUALITY_DASHBOARD_ICEBERG_TABLES,
  type QualityDashboardIcebergTable,
} from '@/shared/api/demo/data/qualityDashboardIcebergData';

import type {
  ColumnQualityApiParams,
  ColumnQualityApiResult,
  DataZoneRowCountApiParams,
  DataZoneRowCountApiResponse,
  DataZoneRowCountApiResult,
  QualityTrendApiParams,
  QualityTrendApiResult,
  StatsTableItem,
  StatsTablesApiResult,
  TableQualityApiParams,
  TableQualityApiResponse,
  TableQualityApiResult,
} from '@/entities/quality/api';
import type { StatsTablesApiParams } from '@/entities/quality/api/getStatsTables';
import type { DataZone, DataZoneLowerCaseString, QualityLevel, SelectedSortByValue } from '@/entities/quality/domain';

type QualityDailyRecord = {
  tableId: number;
  tableName: string;
  fqn: string;
  service: string;
  dataZone: DataZoneLowerCaseString;
  dataZoneLabel: DataZone;
  namespace: string;
  statDate: string;
  totalRows: number;
  incrementalRows: number;
  dailyGoodRows: number;
  dailyPoorRows: number;
  dailyUnknownRows: number;
  cumulativeGoodRows: number;
  cumulativePoorRows: number;
  cumulativeUnknownRows: number;
  goodRatio: number;
  poorRatio: number;
  unknownRatio: number;
  qualityLevel: QualityLevel;
  collectedAt: string;
  columnNames: string[];
};

type QualityColumnRecord = {
  id: number;
  tableId: number;
  tableName: string;
  fqn: string;
  service: string;
  columnName: string;
  dataZone: DataZoneLowerCaseString;
  statDate: string;
  totalRows: number;
  goodRows: number;
  goodRatio: number;
  poorRows: number;
  poorRatio: number;
  unknownRows: number;
  unknownRatio: number;
  incrementalRows: number;
  qualityLevel: QualityLevel;
  collectedAt: string;
};

type QualityCounts = {
  goodRows: number;
  poorRows: number;
  unknownRows: number;
  goodRatio: number;
  poorRatio: number;
  unknownRatio: number;
};

type QualityPercentages = {
  good: number;
  poor: number;
  unknown: number;
};

const HISTORY_DAYS = 60;
const HISTORY_END_DATE = dayjs().subtract(1, 'day').startOf('day');
const HISTORY_START_DATE = HISTORY_END_DATE.subtract(HISTORY_DAYS - 1, 'day');

const TABLE_DAILY_BASELINE_MAP = {
  // Bronze (12)
  kmp_ecms_bearings1: 4,
  gsc_bearings: 2,
  hanwha_impact_bearings1: 4,
  gsp_ecms1_chart: 280,
  kmp_ecms_trend: 46,
  gsp_dcs: 120,
  gsp_ecms1_bearings2: 5,
  gsp_ecms1_bearings1: 5,
  hanwha_impact_chart: 230,
  gsp_ecms1_trend: 52,
  hanwha_impact_trend: 38,
  kmp_ecms_chart: 210,

  // Silver (6)
  gsp_ecms1_waveform_preprocessed: 78,
  hanwha_impact_config: 2,
  kmp_ecms_config: 2,
  hanwha_impact_waveform_preprocessed: 66,
  gsp_ecms1_config: 2,
  kmp_ecms_waveform_preprocessed: 60,

  // Gold (6)
  gsp_ecms1_feature_preprocessed: 12,
  kmp_ecms_feature_preprocessed: 10,
  hanwha_impact_feature_preprocessed: 9,
  kmp_ecms_diagnosis_results: 1,
  gsp_ecms1_diagnosis_result: 1,
  hanwha_impact_diagnosis_results: 1,
} as const;

const DATA_ZONE_RATIO_KEY_MAP: Record<DataZoneLowerCaseString, keyof DataZoneRowCountApiResult['summary']> = {
  bronze: 'bronze',
  silver: 'silver',
  gold: 'gold',
};

const QUALITY_LEVEL_SORT_MAP: Record<QualityLevel, number> = {
  high: 3,
  medium: 2,
  low: 1,
};

const hashString = (value: string): number => {
  let hash = 0;

  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) % 1_000_000_007;
  }

  return Math.abs(hash);
};

const roundToOneDecimal = (value: number): number => {
  return Math.round(value * 10) / 10;
};

const getRatiosFromCounts = ({
  totalRows,
  goodRows,
  poorRows,
}: {
  totalRows: number;
  goodRows: number;
  poorRows: number;
}) => {
  if (totalRows === 0) {
    return {
      goodRatio: 0,
      poorRatio: 0,
      unknownRatio: 0,
    };
  }

  const goodRatio = roundToOneDecimal((goodRows / totalRows) * 100);
  const poorRatio = roundToOneDecimal((poorRows / totalRows) * 100);
  const unknownRatio = Math.max(0, roundToOneDecimal(100 - goodRatio - poorRatio));

  return {
    goodRatio,
    poorRatio,
    unknownRatio,
  };
};

const getQualityLevel = (goodRatio: number): QualityLevel => {
  if (goodRatio >= 95) return 'high';
  if (goodRatio >= 80) return 'medium';
  return 'low';
};

const buildDateRange = (): string[] => {
  return Array.from({ length: HISTORY_DAYS }, (_, index) => HISTORY_START_DATE.add(index, 'day').format('YYYY-MM-DD'));
};

const DATE_RANGE = buildDateRange();

const getWeekdayMultiplier = (statDate: string): number => {
  const dayOfWeek = dayjs(statDate).day();

  if (dayOfWeek === 0) return 0.82;
  if (dayOfWeek === 6) return 0.88;
  if (dayOfWeek === 1) return 1.06;
  if (dayOfWeek === 2) return 1.01;
  if (dayOfWeek === 3) return 1.02;
  if (dayOfWeek === 4) return 1.01;
  return 0.98;
};

const getVarianceMultiplier = (seed: string, min: number, step: number, count: number): number => {
  return min + (hashString(seed) % count) * step;
};

const getDailyIncrementals = (table: QualityDashboardIcebergTable): number[] => {
  const tableDailyBaseline = TABLE_DAILY_BASELINE_MAP[table.tableName as keyof typeof TABLE_DAILY_BASELINE_MAP];

  if (tableDailyBaseline === undefined) {
    console.warn(`[QualityDashboardMockStore] Missing daily baseline map for table: ${table.tableName}`);
    return DATE_RANGE.map(() => 0);
  }

  const rawIncrementals = DATE_RANGE.map((statDate) => {
    const weekdayMultiplier = getWeekdayMultiplier(statDate);
    const varianceMultiplier = getVarianceMultiplier(`${table.tableName}:${statDate}`, 0.98, 0.01, 5);
    const estimatedIncremental = tableDailyBaseline * weekdayMultiplier * varianceMultiplier;

    return Math.max(1, Math.round(estimatedIncremental));
  });

  const rawTotal = rawIncrementals.reduce((acc, value) => acc + value, 0);
  const targetTotal = Math.min(rawTotal, table.latestTotalRows);
  const scale = rawTotal > targetTotal ? targetTotal / rawTotal : 1;

  const normalizedIncrementals = rawIncrementals.map((value) => Math.max(1, Math.round(value * scale)));
  const normalizedTotal = normalizedIncrementals.reduce((acc, value) => acc + value, 0);
  const diff = targetTotal - normalizedTotal;
  const lastIndex = normalizedIncrementals.length - 1;

  normalizedIncrementals[lastIndex] = Math.max(1, normalizedIncrementals[lastIndex] + diff);

  return normalizedIncrementals;
};

const getCountsFromPercentages = (totalRows: number, percentages: QualityPercentages): QualityCounts => {
  const goodRows = Math.round((totalRows * percentages.good) / 100);
  const unknownRows = Math.round((totalRows * percentages.unknown) / 100);
  const poorRows = Math.max(0, totalRows - goodRows - unknownRows);
  const { goodRatio, poorRatio, unknownRatio } = getRatiosFromCounts({
    totalRows,
    goodRows,
    poorRows,
  });

  return {
    goodRows,
    poorRows,
    unknownRows,
    goodRatio,
    poorRatio,
    unknownRatio,
  };
};

const getDailyCountsFromRecord = (record: QualityDailyRecord): QualityCounts => {
  const totalRows = record.incrementalRows;
  const { goodRatio, poorRatio, unknownRatio } = getRatiosFromCounts({
    totalRows,
    goodRows: record.dailyGoodRows,
    poorRows: record.dailyPoorRows,
  });

  return {
    goodRows: record.dailyGoodRows,
    poorRows: record.dailyPoorRows,
    unknownRows: record.dailyUnknownRows,
    goodRatio,
    poorRatio,
    unknownRatio,
  };
};

const buildCollectedAt = (tableName: string, statDate: string): string => {
  const hash = hashString(`${tableName}:${statDate}`);
  const hour = 5 + (hash % 5);
  const minute = Math.floor(hash / 7) % 60;
  const second = Math.floor(hash / 17) % 60;

  return dayjs(statDate).hour(hour).minute(minute).second(second).millisecond(0).toISOString();
};

const TABLE_HISTORY = QUALITY_DASHBOARD_ICEBERG_TABLES.flatMap((table) => {
  const incrementals = getDailyIncrementals(table);
  const startingRows = Math.max(0, table.latestTotalRows - incrementals.reduce((acc, value) => acc + value, 0));

  return DATE_RANGE.reduce<QualityDailyRecord[]>((records, statDate, index) => {
    const previousTotalRows = index === 0 ? startingRows : records[index - 1].totalRows;
    const totalRows = previousTotalRows + incrementals[index];
    const cumulativeCounts = getCountsFromPercentages(totalRows, table.roleQualityRatio);
    const dailyCounts = getCountsFromPercentages(incrementals[index], table.roleQualityRatio);

    records.push({
      tableId: table.tableId,
      tableName: table.tableName,
      fqn: table.fqn,
      service: table.service,
      dataZone: table.dataZone,
      dataZoneLabel: table.dataZoneLabel,
      namespace: table.namespace,
      statDate,
      totalRows,
      incrementalRows: incrementals[index],
      dailyGoodRows: dailyCounts.goodRows,
      dailyPoorRows: dailyCounts.poorRows,
      dailyUnknownRows: dailyCounts.unknownRows,
      cumulativeGoodRows: cumulativeCounts.goodRows,
      cumulativePoorRows: cumulativeCounts.poorRows,
      cumulativeUnknownRows: cumulativeCounts.unknownRows,
      goodRatio: cumulativeCounts.goodRatio,
      poorRatio: cumulativeCounts.poorRatio,
      unknownRatio: cumulativeCounts.unknownRatio,
      qualityLevel: getQualityLevel(cumulativeCounts.goodRatio),
      collectedAt: buildCollectedAt(table.tableName, statDate),
      columnNames: table.columnNames,
    });

    return records;
  }, []);
});

const TABLES_BY_ID = new Map(QUALITY_DASHBOARD_ICEBERG_TABLES.map((table) => [table.tableId, table]));

if (QUALITY_DASHBOARD_ICEBERG_TABLES.length !== 24) {
  console.warn(
    `[QualityDashboardMockStore] Expected 24 iceberg tables, received ${QUALITY_DASHBOARD_ICEBERG_TABLES.length}`
  );
}

const getFilteredHistory = ({
  fromDate,
  toDate,
  dataZone,
  tableId,
}: {
  fromDate?: string;
  toDate?: string;
  dataZone?: string;
  tableId?: number;
}): QualityDailyRecord[] => {
  return TABLE_HISTORY.filter((record) => {
    if (fromDate && record.statDate < fromDate) return false;
    if (toDate && record.statDate > toDate) return false;
    if (dataZone && record.dataZone !== dataZone) return false;
    if (tableId && record.tableId !== tableId) return false;
    return true;
  });
};

const getLatestRecordsByTable = (records: QualityDailyRecord[]): QualityDailyRecord[] => {
  const latestRecordMap = new Map<number, QualityDailyRecord>();

  records.forEach((record) => {
    const current = latestRecordMap.get(record.tableId);

    if (!current || record.statDate > current.statDate) {
      latestRecordMap.set(record.tableId, record);
    }
  });

  return Array.from(latestRecordMap.values());
};

const sortTableQualityRecords = (records: TableQualityApiResponse[], sortBy: SelectedSortByValue = '-collectedAt') => {
  return [...records].sort((left, right) => {
    if (sortBy === '-goodRatio') {
      return right.goodRatio - left.goodRatio || left.tableName.localeCompare(right.tableName);
    }

    if (sortBy === 'goodRatio') {
      return left.goodRatio - right.goodRatio || left.tableName.localeCompare(right.tableName);
    }

    return (
      dayjs(right.collectedAt).valueOf() - dayjs(left.collectedAt).valueOf() ||
      right.tableId - left.tableId ||
      left.tableName.localeCompare(right.tableName)
    );
  });
};

const paginate = <T>(items: T[], page: number = 1, pageSize: number = 20) => {
  const safePage = Math.max(1, page);
  const safePageSize = Math.max(1, pageSize);
  const startIndex = (safePage - 1) * safePageSize;

  return {
    items: items.slice(startIndex, startIndex + safePageSize),
    total: items.length,
    page: safePage,
    pageSize: safePageSize,
    totalPages: items.length === 0 ? 0 : Math.ceil(items.length / safePageSize),
  };
};

const isKeyOrTimeColumn = (columnName: string): boolean => {
  return /(^id$|_id$|^id_|time|date|_dt$|_at$|timestamp|tag_id|chart_id|trend_id|rpm_tag|phase_[xy]_tag)/.test(
    columnName
  );
};

const isRawPayloadColumn = (columnName: string): boolean => {
  return /chart_data|vibration_|phase_|amplitude|bandwidth|max_amplitudes|full_amplitude|full_frequency|gap|process_vib|freq|frequency|lines|sample_rate|sample_size|per_bit/.test(
    columnName
  );
};

const getColumnPercentages = (
  columnName: string,
  baseGoodRatio: number,
  baseUnknownRatio: number
): QualityPercentages => {
  let good = baseGoodRatio;
  let unknown = baseUnknownRatio;

  if (isKeyOrTimeColumn(columnName)) {
    good += 1;
  }

  if (isRawPayloadColumn(columnName)) {
    good -= 2;
    unknown += 1;
  }

  good = Math.max(0, Math.min(100, good));
  unknown = Math.max(0, Math.min(100 - good, unknown));

  const poor = Math.max(0, 100 - good - unknown);
  const ratioSum = good + poor + unknown;

  if (ratioSum === 100) {
    return { good, poor, unknown };
  }

  return {
    good,
    poor: 100 - good - unknown,
    unknown,
  };
};

const toTableQualityRecord = (record: QualityDailyRecord): TableQualityApiResponse => {
  const dailyCounts = getDailyCountsFromRecord(record);

  return {
    tableId: record.tableId,
    tableName: record.tableName,
    fqn: record.fqn,
    service: record.service,
    dataZone: record.dataZone,
    namespace: record.namespace,
    statDate: record.statDate,
    totalRows: record.totalRows,
    goodRows: dailyCounts.goodRows,
    goodRatio: dailyCounts.goodRatio,
    poorRows: dailyCounts.poorRows,
    poorRatio: dailyCounts.poorRatio,
    unknownRows: dailyCounts.unknownRows,
    unknownRatio: dailyCounts.unknownRatio,
    incrementalRows: record.incrementalRows,
    qualityLevel: getQualityLevel(dailyCounts.goodRatio),
    collectedAt: record.collectedAt,
  };
};

const toColumnQualityRecords = (record: QualityDailyRecord): QualityColumnRecord[] => {
  const dailyCounts = getDailyCountsFromRecord(record);

  return record.columnNames.map((columnName, index) => {
    const ratios = getColumnPercentages(columnName, dailyCounts.goodRatio, dailyCounts.unknownRatio);
    const counts = getCountsFromPercentages(record.incrementalRows, ratios);

    return {
      id: record.tableId * 1000 + index + 1,
      tableId: record.tableId,
      tableName: record.tableName,
      fqn: record.fqn,
      service: record.service,
      columnName,
      dataZone: record.dataZone,
      statDate: record.statDate,
      totalRows: record.totalRows,
      goodRows: counts.goodRows,
      goodRatio: counts.goodRatio,
      poorRows: counts.poorRows,
      poorRatio: counts.poorRatio,
      unknownRows: counts.unknownRows,
      unknownRatio: counts.unknownRatio,
      incrementalRows: record.incrementalRows,
      qualityLevel: getQualityLevel(counts.goodRatio),
      collectedAt: record.collectedAt,
    };
  });
};

const getSummaryForRecords = (records: QualityDailyRecord[]) => {
  return records.reduce(
    (acc, record) => {
      acc.totalGoodRows += record.dailyGoodRows;
      acc.totalPoorRows += record.dailyPoorRows;
      acc.totalUnknownRows += record.dailyUnknownRows;
      return acc;
    },
    {
      totalGoodRows: 0,
      totalPoorRows: 0,
      totalUnknownRows: 0,
    }
  );
};

const getZoneSummary = (records: QualityDailyRecord[]) => {
  const latestByTable = getLatestRecordsByTable(records);
  const totals = {
    bronze: { totalRows: 0, tableCount: 0, ratio: 0 },
    silver: { totalRows: 0, tableCount: 0, ratio: 0 },
    gold: { totalRows: 0, tableCount: 0, ratio: 0 },
  };

  latestByTable.forEach((record) => {
    const key = DATA_ZONE_RATIO_KEY_MAP[record.dataZone];
    totals[key].totalRows += record.totalRows;
    totals[key].tableCount += 1;
  });

  const grandTotal = Object.values(totals).reduce((acc, zone) => acc + zone.totalRows, 0);

  (Object.keys(totals) as Array<keyof typeof totals>).forEach((key) => {
    totals[key].ratio = grandTotal === 0 ? 0 : roundToOneDecimal((totals[key].totalRows / grandTotal) * 100);
  });

  return {
    summary: totals,
    grandTotal,
  };
};

const getLatestReferenceDate = (records: QualityDailyRecord[], fallbackDate: string): string => {
  const latestByTable = getLatestRecordsByTable(records);

  if (latestByTable.length === 0) return fallbackDate;

  return latestByTable.reduce((latestDate, record) => {
    return record.statDate > latestDate ? record.statDate : latestDate;
  }, latestByTable[0].statDate);
};

export const getStatsTablesMockData = (params: StatsTablesApiParams = {}): StatsTablesApiResult => {
  const sortBy = params.sortBy ?? 'tableName';

  const filteredTables = QUALITY_DASHBOARD_ICEBERG_TABLES.filter((table) => {
    if (params.dataZone && table.dataZone !== params.dataZone) return false;
    if (params.namespace && table.namespace !== params.namespace) return false;
    return true;
  });

  const sortedTables = [...filteredTables].sort((left, right) => {
    if (sortBy === 'createdAt') return left.updatedAt - right.updatedAt;
    if (sortBy === '-createdAt') return right.updatedAt - left.updatedAt;
    if (sortBy === 'dataZone')
      return left.dataZone.localeCompare(right.dataZone) || left.tableName.localeCompare(right.tableName);
    if (sortBy === 'namespace')
      return left.namespace.localeCompare(right.namespace) || left.tableName.localeCompare(right.tableName);
    if (sortBy === '-tableName') return right.tableName.localeCompare(left.tableName);
    return left.tableName.localeCompare(right.tableName);
  });

  const data: StatsTableItem[] = sortedTables.map((table) => ({
    id: table.tableId,
    fqn: table.fqn,
    dataZone: table.dataZone,
    namespace: table.namespace,
    tableName: table.tableName,
    columns: table.columnNames,
    isActive: true,
    createdAt: dayjs(table.updatedAt).toISOString(),
  }));

  return {
    success: true,
    data,
    meta: {
      total: data.length,
    },
  };
};

export const getDataZoneRowCountMockData = (params: DataZoneRowCountApiParams): DataZoneRowCountApiResult => {
  const dataZone = params.dataZone?.toLowerCase() as DataZoneLowerCaseString | undefined;
  const records = getFilteredHistory({
    fromDate: params.fromDate,
    toDate: params.toDate,
    dataZone,
    tableId: params.tableId,
  });
  const latestRecords = getLatestRecordsByTable(records);
  const rowCountData: DataZoneRowCountApiResponse[] = latestRecords.map((record) => ({
    id: record.tableId,
    tableId: record.tableId,
    fqn: record.fqn,
    dataZone: record.dataZoneLabel,
    namespace: record.namespace,
    tableName: record.tableName,
    statDate: record.statDate,
    countType: params.countType ?? 'total',
    rowCount: params.countType === 'incremental' ? record.incrementalRows : record.totalRows,
    collectedAt: record.collectedAt,
  }));

  const { summary, grandTotal } = getZoneSummary(records);
  const referenceDate = getLatestReferenceDate(records, params.toDate);

  return {
    success: true,
    data: rowCountData,
    meta: {
      total: rowCountData.length,
      referenceDate,
      grandTotal,
    },
    summary,
  };
};

export const getQualityTrendMockData = (params: QualityTrendApiParams): QualityTrendApiResult => {
  const records = getFilteredHistory({
    fromDate: params.fromDate,
    toDate: params.toDate,
    dataZone: params.dataZone,
  });

  const grouped = records.reduce<Map<string, Map<string, QualityDailyRecord[]>>>((acc, record) => {
    if (!acc.has(record.statDate)) {
      acc.set(record.statDate, new Map());
    }

    const zoneMap = acc.get(record.statDate);

    if (!zoneMap) return acc;

    if (!zoneMap.has(record.dataZone)) {
      zoneMap.set(record.dataZone, []);
    }

    zoneMap.get(record.dataZone)?.push(record);

    return acc;
  }, new Map());

  const data = Array.from(grouped.entries())
    .sort(([leftDate], [rightDate]) => leftDate.localeCompare(rightDate))
    .flatMap(([statDate, zoneMap]) => {
      const targetZones = params.dataZone ? [params.dataZone] : Array.from(zoneMap.keys()).sort();

      return targetZones.flatMap((zone) => {
        const zoneRecords = zoneMap.get(zone);
        if (!zoneRecords || zoneRecords.length === 0) return [];

        const totalRows = zoneRecords.reduce((acc, record) => acc + record.incrementalRows, 0);
        const goodRows = zoneRecords.reduce((acc, record) => acc + record.dailyGoodRows, 0);
        const poorRows = zoneRecords.reduce((acc, record) => acc + record.dailyPoorRows, 0);
        const unknownRows = zoneRecords.reduce((acc, record) => acc + record.dailyUnknownRows, 0);

        return [
          {
            statDate,
            dataZone: zone,
            totalRows,
            goodRows,
            poorRows,
            unknownRows,
            goodRatio: totalRows === 0 ? 0 : roundToOneDecimal((goodRows / totalRows) * 100),
          },
        ];
      });
    });

  return {
    success: true,
    data,
    meta: {
      fromDate: params.fromDate,
      toDate: params.toDate,
      days: data.length,
      dataZone: params.dataZone ?? 'all',
    },
  };
};

export const getTableQualityMockData = (params: TableQualityApiParams): TableQualityApiResult => {
  const historyRecords = getFilteredHistory({
    fromDate: params.fromDate,
    toDate: params.toDate,
    dataZone: params.dataZone,
    tableId: params.tableId,
  });
  const latestRecords = getLatestRecordsByTable(historyRecords);
  const transformedRecords = latestRecords.map(toTableQualityRecord);
  const qualityFilteredRecords = params.qualityLevel
    ? transformedRecords.filter((record) => record.qualityLevel === params.qualityLevel)
    : transformedRecords;
  const sortedRecords = sortTableQualityRecords(qualityFilteredRecords, params.sortBy);
  const paginated = paginate(sortedRecords, params.page ?? 1, params.pageSize ?? 20);
  const summary = getSummaryForRecords(historyRecords);

  return {
    success: true,
    data: paginated.items,
    summary,
    meta: {
      total: paginated.total,
      page: paginated.page,
      pageSize: paginated.pageSize,
      totalPages: paginated.totalPages,
      fromDate: params.fromDate,
      toDate: params.toDate,
    },
  };
};

export const getColumnQualityMockData = (params: ColumnQualityApiParams): ColumnQualityApiResult => {
  const historyRecords = getFilteredHistory({
    fromDate: params.fromDate,
    toDate: params.toDate,
    dataZone: params.dataZone,
    tableId: params.tableId,
  });
  const latestRecords = getLatestRecordsByTable(historyRecords);
  const expandedColumns = latestRecords.flatMap(toColumnQualityRecords);
  const filteredColumns = expandedColumns.filter((columnRecord) => {
    if (params.columnName && columnRecord.columnName !== params.columnName) return false;
    if (params.qualityLevel && columnRecord.qualityLevel !== params.qualityLevel) return false;
    return true;
  });

  const sortedColumns = [...filteredColumns].sort((left, right) => {
    if (params.sortBy === '-goodRatio') {
      return right.goodRatio - left.goodRatio || left.columnName.localeCompare(right.columnName);
    }

    if (params.sortBy === 'goodRatio') {
      return left.goodRatio - right.goodRatio || left.columnName.localeCompare(right.columnName);
    }

    return (
      dayjs(right.collectedAt).valueOf() - dayjs(left.collectedAt).valueOf() ||
      QUALITY_LEVEL_SORT_MAP[right.qualityLevel] - QUALITY_LEVEL_SORT_MAP[left.qualityLevel] ||
      left.columnName.localeCompare(right.columnName)
    );
  });

  const paginated = paginate(sortedColumns, params.page, params.pageSize);

  return {
    success: true,
    data: paginated.items,
    meta: {
      total: paginated.total,
      page: paginated.page,
      pageSize: paginated.pageSize,
      totalPages: paginated.totalPages,
      fromDate: params.fromDate,
      toDate: params.toDate,
    },
  };
};

export const getQualityCategoriesMockData = () => {
  const services = QUALITY_DASHBOARD_ICEBERG_TABLES.reduce<Map<string, number>>((acc, table) => {
    acc.set(table.service, (acc.get(table.service) || 0) + 1);
    return acc;
  }, new Map());

  const dataZones = QUALITY_DASHBOARD_ICEBERG_TABLES.reduce<Map<DataZoneLowerCaseString, number>>((acc, table) => {
    acc.set(table.dataZone, (acc.get(table.dataZone) || 0) + 1);
    return acc;
  }, new Map());

  return {
    success: true,
    data: {
      services: Array.from(services.entries()).map(([value, count]) => ({
        value,
        label: value,
        count,
      })),
      dataZones: Array.from(dataZones.entries()).map(([value, count]) => ({
        value,
        label: value,
        count,
      })),
    },
    meta: {},
  };
};

export const getQualityTableById = (tableId: number) => {
  return TABLES_BY_ID.get(tableId);
};
